import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const IndividualAttendanceReport = ({ 
  isOpen, 
  onClose, 
  selectedPerson,
  sectionName, 
  className,
  campusName,
  startDate,
  endDate,
  userType
}) => {
  const reportRef = useRef();

  // Generate dummy attendance data for individual person
  const generateIndividualAttendanceData = (person) => {
    const attendanceStatuses = ["Present", "Absent", "Late", "Excused"];
    const absenceReasons = ["Sick", "Family Emergency", "Transportation Issue", "Personal", "Medical Appointment", "Weather", "Other"];

    // Calculate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const presentDays = Math.floor(Math.random() * (totalDays - 2)) + Math.floor(totalDays * 0.7); // Random between 70%-95% attendance
    const attendancePercentage = Math.round((presentDays / totalDays) * 100);
    
    const attendanceRecords = [];
    const currentDate = new Date(start);
    
    for (let i = 0; i < totalDays; i++) {
      const status = attendanceStatuses[Math.floor(Math.random() * attendanceStatuses.length)];
      const reason = status === "Absent" ? absenceReasons[Math.floor(Math.random() * absenceReasons.length)] : "";
      
      attendanceRecords.push({
        date: currentDate.toISOString().split('T')[0],
        status,
        reason
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
      ...person,
      attendancePercentage,
      weeklyAttendance: Math.floor(Math.random() * 20) + 80, // 80-100%
      monthlyAttendance: attendancePercentage,
      termWiseAttendance: Math.floor(Math.random() * 15) + 85, // 85-100%
      attendanceRecords
    };
  };

  const attendanceData = selectedPerson ? generateIndividualAttendanceData(selectedPerson) : null;
  const academicYear = "2023-24";

  const downloadPDF = () => {
    generatePDFFromElement(
      reportRef.current,
      `${selectedPerson.role}_Attendance_Report_${selectedPerson.name.replace(/\s+/g, '_')}.pdf`
    );
  };

  const downloadExcel = () => {
    if (!attendanceData) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      [`${selectedPerson.role} Attendance Report`],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Academic Year", academicYear],
      ["Date Range", `${new Date(startDate).toLocaleDateString('en-GB')} to ${new Date(endDate).toLocaleDateString('en-GB')}`],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      [],
      ["ID", attendanceData.id],
      ["Name", attendanceData.name],
      ["Role", attendanceData.role],
      ["Weekly Attendance", attendanceData.weeklyAttendance + "%"],
      ["Monthly Attendance", attendanceData.monthlyAttendance + "%"],
      ["Term-wise Attendance", attendanceData.termWiseAttendance + "%"],
      ["Overall Attendance", attendanceData.attendancePercentage + "%"]
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Detailed attendance sheet
    const detailedData = [
      ["Detailed Attendance Records"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Name", attendanceData.name],
      ["ID", attendanceData.id],
      ["Role", attendanceData.role],
      ["Date Range", `${new Date(startDate).toLocaleDateString('en-GB')} to ${new Date(endDate).toLocaleDateString('en-GB')}`],
      [],
      ["Date", "Attendance Status", "Reason for Absence"]
    ];

    attendanceData.attendanceRecords.forEach(record => {
      detailedData.push([
        record.date,
        record.status,
        record.reason
      ]);
    });

    const detailedSheet = XLSX.utils.aoa_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, "Attendance Records");
    
    XLSX.writeFile(workbook, `${selectedPerson.role}_Attendance_Report_${selectedPerson.name.replace(/\s+/g, '_')}.xlsx`);
  };

  if (!isOpen || !attendanceData) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
          <h2 className="text-lg font-semibold mt-2">{selectedPerson.role} Attendance Report</h2>
          <p className="text-base">{academicYear}</p>
          <p className="text-base font-medium">{className} - {sectionName}</p>
          <p className="text-sm text-gray-600">Date Range: {new Date(startDate).toLocaleDateString('en-GB')} to {new Date(endDate).toLocaleDateString('en-GB')}</p>
          <p className="text-sm text-gray-600">Report Generated: {new Date().toLocaleDateString('en-GB')}</p>
        </div>

        {/* Person Info */}
        <div className="mb-6">
          <div className="grid grid-cols-1 gap-y-2">
            <div className="flex">
              <p className="w-32 font-semibold">{selectedPerson.role} ID</p>
              <p className="flex-1 border-b border-gray-400 ml-4">{attendanceData.id}</p>
            </div>
            <div className="flex">
              <p className="w-32 font-semibold">Full Name</p>
              <p className="flex-1 border-b border-gray-400 ml-4">{attendanceData.name}</p>
            </div>
            <div className="flex">
              <p className="w-32 font-semibold">Role</p>
              <p className="w-32 border-b border-gray-400 ml-4">
                <span className={`px-2 py-1 rounded text-xs ${
                  attendanceData.role === 'Teacher' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {attendanceData.role}
                </span>
              </p>
              <p className="w-16 font-semibold ml-8">Date</p>
              <p className="w-32 border-b border-gray-400 ml-4">{new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>
        </div>

        {/* Attendance Summary */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Attendance Summary</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Weekly</h4>
              <div className="text-2xl font-bold text-blue-600">{attendanceData.weeklyAttendance}%</div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Monthly</h4>
              <div className="text-2xl font-bold text-green-600">{attendanceData.monthlyAttendance}%</div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Term-wise</h4>
              <div className="text-2xl font-bold text-purple-600">{attendanceData.termWiseAttendance}%</div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Overall</h4>
              <div className={`text-2xl font-bold ${
                attendanceData.attendancePercentage >= 90 ? 'text-green-600' :
                attendanceData.attendancePercentage >= 80 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {attendanceData.attendancePercentage}%
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Attendance Records */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Daily Attendance Records</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 p-2 text-left">Date</th>
                <th className="border border-gray-400 p-2 text-center">Attendance Status</th>
                <th className="border border-gray-400 p-2 text-left">Reason for Absence</th>
              </tr>
            </thead>
            <tbody>
              {attendanceData.attendanceRecords.map((record, index) => (
                <tr key={index}>
                  <td className="border border-gray-400 p-2">{record.date}</td>
                  <td className="border border-gray-400 p-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      record.status === 'Present' ? 'bg-green-100 text-green-800' :
                      record.status === 'Absent' ? 'bg-red-100 text-red-800' :
                      record.status === 'Late' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {record.status}
                    </span>
                  </td>
                  <td className="border border-gray-400 p-2">{record.reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Attendance Statistics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Attendance Statistics</h3>
          <div className="border border-gray-400 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Working Days:</span>
                  <span>{attendanceData.attendanceRecords.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Present Days:</span>
                  <span className="text-green-600 font-semibold">
                    {attendanceData.attendanceRecords.filter(r => r.status === 'Present').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Absent Days:</span>
                  <span className="text-red-600 font-semibold">
                    {attendanceData.attendanceRecords.filter(r => r.status === 'Absent').length}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Late Days:</span>
                  <span className="text-yellow-600 font-semibold">
                    {attendanceData.attendanceRecords.filter(r => r.status === 'Late').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Excused Days:</span>
                  <span className="text-blue-600 font-semibold">
                    {attendanceData.attendanceRecords.filter(r => r.status === 'Excused').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Attendance Rate:</span>
                  <span className={`font-semibold ${
                    attendanceData.attendancePercentage >= 90 ? 'text-green-600' :
                    attendanceData.attendancePercentage >= 80 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {attendanceData.attendancePercentage}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Lines */}
        <div className="grid grid-cols-3 gap-8 mt-12">
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              {selectedPerson.role === 'Teacher' ? 'Head of Department' : 'Class Teacher'}'s Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              {selectedPerson.role === 'Teacher' ? 'Vice Principal' : 'Head of Department'}'s Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Principal's Signature
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button onClick={downloadPDF} className="bg-blue-500 px-4 py-2 rounded text-white hover:bg-blue-600">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 px-4 py-2 rounded text-white hover:bg-green-600">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 px-4 py-2 rounded text-white hover:bg-gray-500">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndividualAttendanceReport;
